// ============================================================================
// OrganigrammePage / OrgExplorer — /organigramme (E1c, F-232..F-239)
// ============================================================================
// Portage du PoC (organigramme/page.tsx + components/org/org-explorer.tsx) au
// standard maison. La page possede l'etat de VUE (equipe selectionnee, recherche,
// personne centree) et reconstruit la foret COTE CLIENT via le module pur DEJA
// PORTE `lib/org/hierarchy` (buildOrgForest / filterPeopleForTeam) — bascule de vue
// instantanee, sans rechargement (§H.1). On CONSOMME la lib : rien n'est re-porte.
//
// M-217 : AUCUN « mode Publier ». Le module est LECTURE + EXPORT FICHIER (PNG local
// F-235, PDF serveur F-236). Pas de statut brouillon/publie, pas de push babillard.
// Decision gravee 1 : noeuds NON cliquables (surface /profile non portee) — d'ou
// `href: ''` ci-dessous, jamais rendu (voir OrgNodeCard).
// Decision gravee 2 : avatars = initiales (blob Firebase non migre) — `avatar_url: null`.

import { useMemo, useRef, useState } from 'react';
import { PageHeader, EmptyState, SkeletonCard, QueryError, GS_COMPONENTS, GS_COLORS } from '@gs/ui';
import { FileText, Image as ImageIcon, Network, Search, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import {
  buildOrgForest,
  filterPeopleForTeam,
  type OrgPerson,
  type OrgTeamLike,
} from '../../lib/org/hierarchy';
import { getInitials } from '../../lib/profile/initials';
import { useOrg, useExportOrgPdf, type OrgPersonDto, type OrgResponse } from '../../hooks/useOrg';
import { OrgChart, type OrgFocus } from '../../components/org/OrgChart';

const ALL = 'all';
const MAX_RESULTS = 8;

/** Recherche accent-insensible et casse-insensible (verbatim PoC). */
const fold = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

const slug = (s: string): string =>
  fold(s).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'organigramme';

/** Ecran de refus (Q1 corporate-sans-GEST) — l'autorite est le 403 serveur. */
function OrgForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:organigramme.forbidden.title')}
      description={t('gest:organigramme.forbidden.description')}
    />
  );
}

/**
 * Adapte les lignes plates de l'API au type d'entree de la lib DEJA portee.
 * `role` vient du Systeme C REEL (LEFT JOIN ug.UserCompanyBcRoles cote serveur) puis
 * de la meme derivation qu'ailleurs : directeur d'equipe (donnee) > GESTIONNAIRE >
 * UTILISATEUR/aucun. `href`/`avatar_url` sont neutres par DECISION (1 et 2), pas par
 * oubli — la lib les exige structurellement mais l'UI E1c ne les lit jamais.
 */
function toOrgPeople(data: OrgResponse): OrgPerson[] {
  const directorEmails = new Set(
    data.teams.flatMap((t) => (t.directorEmail ? [fold(t.directorEmail)] : [])),
  );
  const roleOf = (p: OrgPersonDto): OrgPerson['role'] => {
    if (p.systemCRole === 'ADMIN') return 'admin';
    if (directorEmails.has(fold(p.email))) return 'director';
    if (p.systemCRole === 'GESTIONNAIRE') return 'manager';
    return 'employee';
  };
  return data.people.map((p) => ({
    email: p.email,
    full_name: p.fullName,
    job_title: p.jobTitle,
    role: roleOf(p),
    committee: p.committee === 'ce' || p.committee === 'cg' ? p.committee : null,
    team_id: p.teamId,
    manager_email: p.managerEmail,
    avatar_url: null, // decision 2 — blob Firebase non migre, initiales seulement
    href: '', // decision 1 — surface /profile non portee, noeuds non cliquables
  }));
}

export default function OrganigrammePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const orgQ = useOrg(!!user);
  const exportPdf = useExportOrgPdf();

  const [teamId, setTeamId] = useState<string>(ALL);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightedEmail, setHighlightedEmail] = useState<string | null>(null);
  const [focus, setFocus] = useState<OrgFocus | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const treeRef = useRef<HTMLDivElement>(null);
  const focusKey = useRef(0);

  const forbidden = orgQ.isError && orgQ.error instanceof ApiForbiddenError;

  const people = useMemo(() => (orgQ.data ? toOrgPeople(orgQ.data) : []), [orgQ.data]);
  const teams: OrgTeamLike[] = useMemo(
    () =>
      (orgQ.data?.teams ?? []).map((tm) => ({
        id: tm.id,
        name: tm.name,
        director_email: tm.directorEmail,
      })),
    [orgQ.data],
  );

  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [teams],
  );
  const selectedTeam = teamId === ALL ? null : teams.find((tm) => tm.id === teamId) ?? null;

  // F-233 : filtre equipe -> sous-ensemble + rebuild CLIENT (aucun rechargement).
  const viewPeople = useMemo(
    () => (selectedTeam ? filterPeopleForTeam(people, selectedTeam) : people),
    [people, selectedTeam],
  );
  const forest = useMemo(() => buildOrgForest(viewPeople, teams), [viewPeople, teams]);

  // F-234 : recherche sur TOUTE l'organisation (on peut trouver quelqu'un hors de la
  // vue equipe courante ; la selection rebascule alors en vue globale).
  const results = useMemo(() => {
    const q = fold(query);
    if (!q) return [];
    return people.filter((p) => fold(p.full_name ?? p.email).includes(q)).slice(0, MAX_RESULTS);
  }, [people, query]);

  function selectPerson(p: OrgPerson) {
    const inView = viewPeople.some((v) => v.email === p.email);
    if (!inView) setTeamId(ALL);
    setHighlightedEmail(p.email);
    focusKey.current += 1;
    setFocus({ email: p.email, key: focusKey.current });
    setQuery(p.full_name ?? p.email);
    setSearchOpen(false);
  }

  /** Capture du canvas (F-235/F-236) — html-to-image en import DYNAMIQUE (hors bundle initial). */
  async function capturePng(): Promise<string | null> {
    const el = treeRef.current;
    if (!el) return null;
    const canvas = el.closest('[data-org-canvas]') as HTMLElement | null;
    // Fond de la capture = celui du canvas reel (suit le dark mode). Repli token
    // si le canvas est introuvable — jamais un PNG transparent (illisible en PDF).
    const backgroundColor = canvas ? getComputedStyle(canvas).backgroundColor : GS_COLORS.white;
    const { toPng } = await import('html-to-image');
    return toPng(el, { pixelRatio: 2, backgroundColor, cacheBust: true });
  }

  function triggerDownload(href: string, filename: string) {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const suffix = selectedTeam ? `-${slug(selectedTeam.name)}` : '';

  /** F-235 : export PNG — 100 % CLIENT, aucun serveur, aucun audit. */
  async function exportPng() {
    setExporting(true);
    setExportError(null);
    try {
      const dataUrl = await capturePng();
      if (!dataUrl) return;
      triggerDownload(dataUrl, `organigramme${suffix}.png`);
    } catch {
      setExportError('png');
    } finally {
      setExporting(false);
    }
  }

  /** F-236 / M-214 : capture client -> POST serveur (gabarit brande + audit) -> download. */
  async function exportPdfFile() {
    setExporting(true);
    setExportError(null);
    let url: string | null = null;
    try {
      const image = await capturePng();
      if (!image) return;
      // On poste le team_id : le TITRE du PDF est resolu serveur (jamais un libelle client).
      const blob = await exportPdf.mutateAsync({ image, teamId: selectedTeam?.id ?? null });
      url = URL.createObjectURL(blob);
      triggerDownload(url, `organigramme${suffix}.pdf`);
    } catch {
      setExportError('pdf');
    } finally {
      // Revoke APRES le clic de telechargement — sinon Safari annule le download.
      if (url) URL.revokeObjectURL(url);
      setExporting(false);
    }
  }

  const canExport = !!orgQ.data && forest.roots.length > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:organigramme.title')}
        description={
          orgQ.data
            ? t('gest:organigramme.subtitle', {
                teams: orgQ.data.teams.length,
                people: orgQ.data.people.length,
              })
            : undefined
        }
      />

      {exportError ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {t(`gest:organigramme.errors.${exportError === 'pdf' ? 'exportPdf' : 'exportPng'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <OrgForbidden />
      ) : orgQ.isLoading ? (
        <SkeletonCard />
      ) : orgQ.isError ? (
        <QueryError message={t('gest:organigramme.loadError')} onRetry={() => void orgQ.refetch()} />
      ) : orgQ.data ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {/* F-233 : filtre equipe */}
              <select
                className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-2 text-field text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                aria-label={t('gest:organigramme.filter.label')}
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
              >
                <option value={ALL}>{t('gest:organigramme.filter.all')}</option>
                {sortedTeams.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.name}
                  </option>
                ))}
              </select>

              {/* F-234 : recherche + centrage */}
              <div
                className="relative w-full sm:w-64"
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSearchOpen(false);
                }}
              >
                <Search
                  size={14}
                  aria-hidden="true"
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-field text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  type="search"
                  placeholder={t('gest:organigramme.search.placeholder')}
                  aria-label={t('gest:organigramme.search.placeholder')}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && results[0]) {
                      e.preventDefault();
                      selectPerson(results[0]);
                    } else if (e.key === 'Escape') {
                      setSearchOpen(false);
                    }
                  }}
                />
                {searchOpen && results.length > 0 ? (
                  <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    {results.map((p) => (
                      <li key={p.email}>
                        <button
                          type="button"
                          onClick={() => selectPerson(p)}
                          className="flex w-full touch-manipulation items-center gap-2 px-2.5 py-1.5 text-left text-field hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <span
                            aria-hidden="true"
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-chip font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                          >
                            {getInitials(p.full_name, p.email)}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
                            {p.full_name ?? p.email}
                            {p.job_title ? (
                              <span className="text-slate-500 dark:text-slate-400"> · {p.job_title}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            {/* F-235 / F-236 : exports. M-217 — AUCUN bouton « Publier » : il n'existe pas. */}
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => void exportPng()}
                disabled={exporting || !canExport}
                className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
              >
                <ImageIcon size={16} aria-hidden="true" />
                {t('gest:organigramme.export.png')}
              </button>
              <button
                type="button"
                onClick={() => void exportPdfFile()}
                disabled={exporting || !canExport}
                className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
              >
                <FileText size={16} aria-hidden="true" />
                {t('gest:organigramme.export.pdf')}
              </button>
            </div>
          </div>

          {orgQ.data.people.length === 0 ? (
            <EmptyState
              icon={<Network size={48} aria-hidden="true" />}
              title={t('gest:organigramme.empty')}
            />
          ) : (
            <OrgChart
              ref={treeRef}
              roots={forest.roots}
              highlightedEmail={highlightedEmail}
              focus={focus}
            />
          )}

          {/* F-239 : « Sans rattachement » — vue globale UNIQUEMENT. */}
          {teamId === ALL && forest.unattached.length > 0 ? (
            <section className={`${GS_COMPONENTS.card} p-4 dark:border-slate-700 dark:bg-slate-800`}>
              <h2 className="text-field font-semibold text-slate-900 dark:text-slate-100">
                {t('gest:organigramme.unattached.title')}
              </h2>
              <p className="mt-0.5 text-chip text-slate-500 dark:text-slate-400">
                {t('gest:organigramme.unattached.description')}
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {forest.unattached.map((node) => (
                  <li key={node.email} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-chip font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                    >
                      {getInitials(node.full_name, node.email)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="block truncate text-field text-slate-900 dark:text-slate-100">
                        {node.full_name ?? node.email}
                      </span>
                      <span className="block truncate text-chip text-slate-500 dark:text-slate-400">
                        {node.job_title ?? node.email}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
